import React, { useEffect, useState, useCallback, useReducer, useMemo } from 'react';
import ZoomVideo, { ConnectionState } from '@zoom/videosdk';
import { MediaStream } from 'index-types';
import ZoomContext from 'zoom/context/zoom-context';
import ZoomMediaContext from 'zoom/context/media-context';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { mediaReducer, mediaShape } from 'shared/constants/zoom-constants';
import { selectMeetingsArgs } from 'redux/selectors/selectors';

declare global {
  interface Window {
    webEndpoint: string | undefined;
    zmClient: any | undefined;
    mediaStream: any | undefined;
    crossOriginIsolated: boolean;
    ltClient: any | undefined;
  }
}

const ZoomSessionWrapper: React.FunctionComponent = (props) => {
  const meetingArgs = useAppSelector(selectMeetingsArgs);

  const zmClient = ZoomVideo.createClient();

  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [mediaState, setMediaState] = useReducer(mediaReducer, mediaShape);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [participants, setParticipants] = useState([]);
  let webEndpoint: any;
  if (meetingArgs.webEndpoint) {
    webEndpoint = meetingArgs.webEndpoint;
  } else {
    webEndpoint = window?.webEndpoint ?? 'zoom.us';
  }
  const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);

  useEffect(() => {
    const init = async () => {
      await zmClient.init('en-US', `${window.location.origin}/lib`, {
        enforceMultipleVideos: true,
        webEndpoint,
        stayAwake: true,
      });
      try {
        if (meetingArgs.signature && meetingArgs.topic && meetingArgs.userIdentity) {
          await zmClient
            .join(meetingArgs.topic, meetingArgs.signature, meetingArgs.userIdentity)
            .catch((e: any) => {
              window.console.log(e);
            });
          setLoggedIn(true);
          const stream = zmClient.getMediaStream();
          setMediaStream(stream);
        }
      } catch (e: any) {
        window.console.error(e.reason);
      }
    };
    init();
    return () => {
      ZoomVideo.destroyClient();
    };
  }, [zmClient, webEndpoint, meetingArgs.topic, meetingArgs.signature, meetingArgs.userIdentity]);

  const onConnectionChange = useCallback(
    (payload: any) => {
      if (payload.state === ConnectionState.Connected) {
        window.zmClient = zmClient;
        window.mediaStream = zmClient.getMediaStream();

        // eslint-disable-next-line
        console.log('getSessionInfo', zmClient.getSessionInfo());
      } else if (payload.state === ConnectionState.Closed) {
        setMediaState({ type: 'reset-media' });
      }
    },
    [zmClient],
  );

  const onMediaSDKChange = useCallback((payload: any) => {
    const { action, type, result } = payload;
    setMediaState({ type: `${type}-${action}`, payload: result === 'success' });
  }, []);

  const onDialoutChange = useCallback((payload: any) => {
    window.console.log('onDialoutChange', payload);
  }, []);

  const onAudioMerged = useCallback((payload: any) => {
    window.console.log('onAudioMerged', payload);
  }, []);

  useEffect(() => {
    if (loggedIn) {
      zmClient.on('connection-change', onConnectionChange);
      zmClient.on('media-sdk-change', onMediaSDKChange);
      zmClient.on('dialout-state-change', onDialoutChange);
      zmClient.on('merged-audio', onAudioMerged);
      return () => {
        zmClient.off('connection-change', onConnectionChange);
        zmClient.off('media-sdk-change', onMediaSDKChange);
        zmClient.off('dialout-state-change', onDialoutChange);
        zmClient.off('merged-audio', onAudioMerged);
      };
    }
  }, [zmClient, onConnectionChange, onMediaSDKChange, onDialoutChange, onAudioMerged, loggedIn]);

  useEffect(() => {
    zmClient.on('user-added', (payload) => {
      setParticipants([...participants, payload]);
    });
  }, [participants, zmClient]);

  return (
    <ZoomContext.Provider value={zmClient}>
      <ZoomMediaContext.Provider value={mediaContext}>{props.children}</ZoomMediaContext.Provider>
    </ZoomContext.Provider>
  );
};

export default ZoomSessionWrapper;
